/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    DropDown,
    getFilterElementsDefault,
    useErrorState,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import { getSelectList } from 'axios/dictionaryApi';
import {
    addPrescrItem,
    deletePrescrItem,
    getPrescr,
    getPrescrExcel,
    getPrescrItem,
    updatePrescrItem,
} from 'axios/prescrApi';
import { getTradeOrgList } from 'axios/questionaryApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { PrescrFilter as PrescrFilterType, PrescrItem, PrescrTable } from 'models/api/prescr';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { checkboxFormatter, deleteActionBtnFormatter } from 'utils/columnFormatters';
import PrescrAddButton from './PrescrAddButton';
import PrescrFilter from './PrescrFilter';
import PrescrModal from './PrescrModal';

const Prescr = () => {
    const { t } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const { handleRequestError } = useErrorState();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { hasRole } = useAuth();
    const [prescrRuleTypes, setPrescrRuleTypes] = useState<DropDown[]>([]);
    const [coverageTypes, setCoverageTypes] = useState<DropDown[]>([]);

    const { isFetching: prescrRulesLoading } = useQuery(
        'PrescrRuleTypes',
        () => getSelectList(api, ['PrescrRuleTypes']),
        {
            onSuccess: (data) => {
                setPrescrRuleTypes(data?.['PrescrRuleTypes'] ?? []);
            },
            onError: (err: AxiosError<AxiosErrorResponse>) => {
                handleRequestError(err);
            },
        }
    );

    const { isFetching: tradeOrgsLoading } = useQuery(
        'tradeOrgs',
        () => getTradeOrgList(api, null),
        {
            onSuccess: (data) => {
                setCoverageTypes(data.map((x) => ({ ...x, id: x.id?.toString() || null })) ?? []);
            },
            onError: (err: AxiosError<AxiosErrorResponse>) => {
                handleRequestError(err);
            },
        }
    );

    return (
        <BOWorkplace<PrescrTable, PrescrItem, PrescrFilterType>
            testId="prescr"
            pageTitle="BackOfficePrescoring"
            filterParams={{
                id: null,
                type: null,
                tradeOrgId: null,
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                },
                {
                    key: 'isActive',
                    name: <Trans i18nKey="BackOfficeIsActive" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    formatter: checkboxFormatter('isActive'),
                },
                { key: 'type', name: <Trans i18nKey="BackOfficeRuleType" /> },
                { key: 'tradeOrgCoverage', name: <Trans i18nKey="BackOfficeTradeOrgCoverage" /> },
                { key: 'tradeOrgs', name: <Trans i18nKey="BackOfficeTradeOrgShort" /> },
                { key: 'priority', name: <Trans i18nKey="BackOfficePriority" /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: deleteActionBtnFormatter(hasRole('prescrDelete')),
                },
            ]}
            storagePath="prescr/columns"
            getFilterElements={getFilterElementsDefault([
                { id: 'id', name: t('BackOfficeId') },
                {
                    id: 'type',
                    formatter: (val) =>
                        `${t('BackOfficeRuleType')}: ${
                            prescrRuleTypes.find((x) => x.id === val)?.name ?? val
                        }`,
                },
                {
                    id: 'tradeOrgId',
                    formatter: (val) =>
                        `${t('BackOfficeTradeOrgShort')}: ${
                            coverageTypes.find((x) => x.id === val)?.name ?? val
                        }`,
                },
            ])}
            showModal={hasRole('prescrGetById') ? () => setIsModalVisible(true) : undefined}
            buttons={
                hasRole('prescrCreate')
                    ? [<PrescrAddButton key="1" showModal={() => setIsModalVisible(true)} />]
                    : []
            }
            filter={
                <PrescrFilter
                    prescrRules={prescrRuleTypes}
                    tradeOrgs={coverageTypes}
                    prescrRulesLoading={prescrRulesLoading}
                    tradeOrgsLoading={tradeOrgsLoading}
                />
            }
            modal={{
                component: isModalVisible && (
                    <PrescrModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                fetchCurrentItemQueryFn: (id) => getPrescrItem(api, id),
                changeMutationFn: (item) => updatePrescrItem(api, item.id ?? 0, item),
                addMutationFn: (item) => addPrescrItem(api, item),
            }}
            deleteMutationFn={(id) => deletePrescrItem(api, id)}
            fetchQueryFn={(filter) => getPrescr(api, filter)}
            queryKey={['prescr']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getPrescrExcel(api, filter)}
            showExcelBtn={hasRole('prescrExcel')}
        />
    );
};

const usePrescrWorkplaceState = () => useWorkplaceState<PrescrTable, PrescrItem>();

export default Prescr;
export { usePrescrWorkplaceState };
